import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { loadViewerPreferences, normalizeViewerPreferences, parseViewerPreferences, saveViewerPreferences, viewerPreferencesKey, type ViewerPreferences } from "./preferences";

const CHANGED = "neoma-viewer-preferences-changed";
let revision = 0;
type Change = { identity: string; value: ViewerPreferences; revision: number; persisted: boolean };

/** Identity/generation fences prevent old callbacks and events from publishing after account changes. */
export function useViewerPreferences(identity: string, enabled = true): [ViewerPreferences, Dispatch<SetStateAction<ViewerPreferences>>] {
  const boundary = useRef({identity, enabled, generation:0});
  if (boundary.current.identity !== identity || boundary.current.enabled !== enabled) {
    boundary.current = {identity, enabled, generation:boundary.current.generation+1};
  }
  const generation = boundary.current.generation;
  const [state, setState] = useState(() => ({identity, generation, value:enabled?loadViewerPreferences(identity):parseViewerPreferences(null)}));
  const value = state.identity === identity && state.generation === generation ? state.value : enabled?loadViewerPreferences(identity):parseViewerPreferences(null);
  const latest = useRef({identity, generation, value});
  latest.current = {identity, generation, value};
  const alive = useRef(true);
  const current = useCallback(() => enabled && alive.current && boundary.current.identity === identity && boundary.current.generation === generation, [enabled,identity,generation]);
  useEffect(() => {
    if (enabled && (state.identity !== identity || state.generation !== generation)) setState({identity,generation,value:loadViewerPreferences(identity)});
  }, [enabled,identity,generation,state.identity,state.generation]);
  useEffect(() => {
    alive.current = true;
    const receive = (event: Event) => {
      if (!current()) return;
      const change = (event as CustomEvent<Change>).detail;
      if (event instanceof StorageEvent) {
        revision++; // Invalidate delayed same-window snapshots, including other identity keys.
        if (event.key !== null && event.key !== viewerPreferencesKey(identity)) return;
        try { window.localStorage.getItem(viewerPreferencesKey(identity)); } catch { return; }
      } else if (!change || change.identity !== identity || change.revision !== revision) return;
      // Never publish event.newValue: an older cross-tab event may arrive after newer storage.
      const next = {identity,generation,value:change && !change.persisted ? normalizeViewerPreferences(change.value) : loadViewerPreferences(identity)};
      latest.current = next; setState(next);
    };
    if (enabled) {
      window.addEventListener(CHANGED, receive);
      window.addEventListener("storage", receive);
    }
    return () => {alive.current=false;window.removeEventListener(CHANGED,receive);window.removeEventListener("storage",receive);};
  }, [enabled,identity,generation,current]);
  return [value, update => {
    if (!current()) return;
    const previous = latest.current.value;
    const proposed = typeof update === "function" ? update(previous) : update;
    if (proposed === previous) return;
    const next = normalizeViewerPreferences(proposed);
    if (JSON.stringify(next) === JSON.stringify(previous)) return;
    latest.current = {identity,generation,value:next};
    const persisted = saveViewerPreferences(identity,next);
    setState(latest.current);
    window.dispatchEvent(new CustomEvent<Change>(CHANGED,{detail:{identity,value:next,revision:++revision,persisted}}));
  }];
}
